import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import cl from 'classnames';
import { useLanguage } from 'src/hooks';
import { Select } from 'src/components/general';
import styles from './paginatorStyle.module.scss';

type PaginatorProps = {
  currentPage: number;
  lastPage: number;
  setPrevPage: () => void;
  setNextPage: () => void;
  countPerPage: number;
  countsPerPages: number[];
  changeCountPerPage: (value: number) => void;
  selectPage: (value: number) => void;
  className?: string;
};

export const Paginator = ({
  currentPage,
  lastPage,
  setPrevPage,
  setNextPage,
  countPerPage,
  countsPerPages,
  changeCountPerPage,
  selectPage,
  className,
}: PaginatorProps) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocumentClick = (event: Event) => {
      if (
        event.target instanceof Node &&
        listRef.current &&
        !listRef.current.contains(event.target)
      ) {
        setExpanded(false);
      }
    };

    if (expanded) {
      document.addEventListener('click', onDocumentClick);
    }

    return () => {
      if (expanded) {
        document.removeEventListener('click', onDocumentClick);
      }
    };
  }, [expanded]);

  const list = useMemo(
    () => new Array(lastPage).fill(null).map((_, index) => index + 1),
    [lastPage],
  );

  return (
    <div className={cl(styles.container, className)}>
      <div className={styles.paginator}>
        <div
          className={cl(
            styles.paginatorButton,
            currentPage === 1 && styles.inactive,
          )}
          onClick={setPrevPage}
        >
          <svg
            width="6"
            height="12"
            viewBox="0 0 6 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.142489 6.39836L4.8873 11.8348C5.07661 12.0551 5.38273 12.0551 5.57204 11.8348L5.85802 11.5021C6.04733 11.2818 6.04733 10.9256 5.85802 10.7053L1.73752 6L5.85399 1.29467C6.0433 1.0744 6.0433 0.71822 5.85399 0.497951L5.56801 0.165203C5.3787 -0.0550661 5.07259 -0.0550661 4.88328 0.165203L0.138461 5.60164C-0.0468204 5.82191 -0.0468204 6.17809 0.142489 6.39836Z"
              fill="white"
            />
          </svg>
        </div>
        <div
          className={cl(styles.number, lastPage === 1 && styles.inactive)}
          onClick={(event) => {
            if (lastPage === 1) {
              return;
            }

            event.stopPropagation();
            setExpanded((prevState) => !prevState);
          }}
        >
          {t('paginator.current', {
            currentPage,
            lastPage,
          })}
          {lastPage !== 1 && (
            <svg
              width="9"
              height="9"
              viewBox="0 0 9 9"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={cl(styles.arrowIcon, expanded && styles.expanded)}
            >
              <path
                d="M6.82056 2.87012L4.30774 5.62225L1.79492 2.87012"
                stroke="white"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          <div
            className={cl(styles.list, expanded && styles.expanded)}
            onClick={(e) => {
              e.stopPropagation();
            }}
            ref={listRef}
          >
            <div style={{ minHeight: 0 }}>
              {list.map((value) => (
                <div
                  key={`pg-select-${value}`}
                  className={styles.listItem}
                  onClick={() => {
                    selectPage(value);
                    setExpanded(false);
                  }}
                >
                  {value}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div
          className={cl(
            styles.paginatorButton,
            currentPage === lastPage && styles.inactive,
          )}
          onClick={setNextPage}
        >
          <svg
            width="6"
            height="12"
            viewBox="0 0 6 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.85751 5.60164L1.1127 0.165202C0.923387 -0.0550673 0.617269 -0.0550673 0.42796 0.165202L0.141982 0.49795C-0.0473274 0.718219 -0.0473274 1.0744 0.141982 1.29467L4.26248 6L0.14601 10.7053C-0.0432997 10.9256 -0.0432997 11.2818 0.14601 11.502L0.431987 11.8348C0.621296 12.0551 0.927413 12.0551 1.11672 11.8348L5.86154 6.39836C6.04682 6.17809 6.04682 5.82191 5.85751 5.60164Z"
              fill="white"
            />
          </svg>
        </div>
      </div>
      <Select
        value={countPerPage}
        items={countsPerPages.map((count) => ({
          value: count,
          label:
            count === Infinity
              ? t('paginator.all')
              : t('paginator.select', { count }),
        }))}
        selectItem={(item) => {
          changeCountPerPage(item as number);
        }}
        design="border"
        className={styles.countsPerPage}
        expandOnTop
      />
    </div>
  );
};

export default memo(Paginator);
